import { Box, Typography, Divider } from '@mui/material';
import { reviewForm } from '@uplift/api';
import { FormElement } from '@uplift/types';
import { Button } from '@uplift/ui-v2';
import { useEffect } from 'react';
import { useParams } from 'react-router';
import { Link } from 'react-router-dom';
import { Loading } from '../../loading/loading.component';
import { getAnswerValue } from '../helpers';
import { useInitializedRenderFormContext } from '../render/render-form.context';
import { RenderFormRouteParams } from '../types';

type ElementsDisplay = {
  group: string;
  elements: {
    formElementId: string;
    text: string;
    answerText: string;
  }[];
}[];

const pageTypes = [ 'page', 'review_page', 'divider', 'button_page' ];

export type ReviewPageProps = {
  isPreview?: boolean;
};

export const ReviewPage: React.VFC<ReviewPageProps> = ({ isPreview }) => {
  const { state, dispatch } = useInitializedRenderFormContext();
  const { elements, groups, submission } = state;
  const { formId } = useParams<RenderFormRouteParams>();

  useEffect(() => {
    if (elements?.length && !state.shouldRefetch) {
      return;
    }

    dispatch({
      type: 'getReviewPage',
    });

    reviewForm({ submission_id: state.submissionId ?? '' })
      .then(response => {
        if ('error' in response) {
          dispatch({
            type: 'setError',
            payload: response.error
          });
        } else {
          dispatch({
            type: 'setReviewPage',
            payload: {
              elements: response.elements,
              submission: response.responses,
            }
          });
        }
      });
  }, [ dispatch, formId, elements, state.shouldRefetch, state.submissionId ]);

  if (isPreview && !elements?.length) {
    return <Loading />;
  } else if (!isPreview && !elements?.length && !submission) {
    return <Loading />;
  }

  const handleEditClick = (elementId: string) => {
    const element = elements.find(x => x.formElementId === elementId);

    if (!element) return;

    const submission = getResponse(elementId);

    if (!submission) return;

    const answerVal = getAnswerValue(element, submission);

    dispatch({
      type: 'setEditElement',
      payload: {
        element,
        answerVal,
      }
    });
  };

  const getPage = (id: string) => elements.find(x => x.formElementId === id)?.orderNumber;
  const getResponse = (id: string) => submission?.responses.find(x => x.formElementId === id);

  const getAnswer = (e: FormElement) => {
    if (isPreview) {
      return '';
    }

    switch (e.formElementDefinition.formElementType) {
      case 'simple_input':
      case 'short_answer':
        return getResponse(e.formElementId)?.responseText;
      case 'single_select':
        return e.formElementAnswers
          .find(x => x.formElementAnswerId === getResponse(x.formElementId)?.formElementAnswerId)
          ?.answerText;
      case 'multi_select':
        return [
          ...e.formElementAnswers
            .filter(x => getResponse(e.formElementId)?.formElementAnswerId?.split('|')?.includes(x.formElementAnswerId))
            .map(x => x.answerText),
          getResponse(e.formElementId)?.responseText
        ].filter(x => x).join(', ');
    }
  };

  const toRender = elements.reduce((acc, el) => {
    if (pageTypes.includes(el.formElementDefinition?.formElementType)) {
      return acc;
    }

    const elementGroup = groups.find(x => x.formGroupId === el.formGroupId);
    const groupObj = acc.find(x => x.group === elementGroup?.name);

    if (!isPreview && !getResponse(el.formElementId)) {
      return acc;
    }

    const displayEl = {
      formElementId: el.formElementId,
      text: el.shortText || el.elementText,
      answerText: getAnswer(el) ?? ''
    };

    if (!groupObj) {
      return [
        ...acc,
        {
          group: elementGroup?.name ?? '',
          elements: [ displayEl ],
        },
      ];
    } else {
      groupObj.elements = groupObj.elements.concat(displayEl);

      return acc;
    }
  }, [] as ElementsDisplay);

  return (
    <>
      {toRender.map((e, eIndex) => (
        <Box mb={1} key={`${e.group}-${eIndex}`}>
          <Typography variant="h6">{e.group}</Typography>
          {e.elements.map(el => (
            <Box mb={2} key={el.formElementId}>
              <Box mb={1} display="flex" justifyContent="space-between">
                <Typography variant="body2">
                  <b>{el.text}</b>
                </Typography>
                <Button
                  type="button"
                  variant="text"
                  color="primary"
                  component={Link}
                  to={`/form/${state.submissionId}/page/${getPage(el.formElementId)}`}
                  onClick={() => handleEditClick(el.formElementId)}
                >
                  Edit
                </Button>
              </Box>
              <Box>{el.answerText}</Box>
            </Box>
          ))}
          <Divider />
        </Box>
      ))}
    </>
  );
};
